import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { mapSubscriptionItems } from '../../utils/subscriptionMapper';

const initialState = {
  // Raw items from the API; views map them through the feed's selector
  items: [],
  refreshedAt: null,
  // The API's explanation when nothing is listed ("No open IPOs found on NSE at this time.")
  note: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

/**
 * One category-wise subscription feed: a thunk, its reducer and a selector of
 * sorted, display-ready rows (utils/subscriptionMapper.js). Both feeds return
 * the same item shape, so only the endpoint and state key differ.
 *
 * The thunk takes options.maxAgeMs: skip the request when data this recent is already loaded.
 */
const createSubscriptionFeed = ({ stateKey, typePrefix, request, label, mapRows = (rows) => rows }) => {
  const fetchFeed = createAsyncThunk(
    typePrefix,
    async (_options, { rejectWithValue }) => {
      try {
        const data = await request();
        if (data?.success === false) {
          return rejectWithValue(data.error || `Failed to fetch ${label}`);
        }
        return data;
      } catch (error) {
        return rejectWithValue(error.message || `Failed to fetch ${label}`);
      }
    },
    {
      condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
        const { status, fetchedAt } = getState()[stateKey];
        // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
        if (status === 'loading') return false;
        if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
          return false;
        }
        return true;
      },
    }
  );

  const slice = createSlice({
    name: stateKey,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchFeed.pending, (state) => {
          state.status = 'loading';
          state.error = null;
        })
        .addCase(fetchFeed.fulfilled, (state, action) => {
          const data = action.payload?.data || {};
          state.status = 'succeeded';
          state.items = Array.isArray(data.items) ? data.items : [];
          state.refreshedAt = data.refreshed_at || null;
          state.note = data.note || null;
          state.fetchedAt = Date.now();
        })
        .addCase(fetchFeed.rejected, (state, action) => {
          // Keep previously loaded items so a failed refresh doesn't blank the section
          state.status = 'failed';
          state.error = action.payload || `Failed to load ${label}`;
        });
    },
  });

  const selectRows = createSelector([(state) => state[stateKey].items], (items) => mapRows(mapSubscriptionItems(items)));

  return { fetchFeed, reducer: slice.reducer, selectRows };
};

// GET /api/market/subscription -- every IPO NSE is tracking (state.subscription)
const subscriptionFeed = createSubscriptionFeed({
  stateKey: 'subscription',
  typePrefix: 'subscription/fetchSubscriptionSummary',
  request: () => ipoService.getSubscriptionSummary(),
  label: 'subscription data',
});

// GET /api/market/subscription/sme -- SME issues only (state.smeSubscription). Every
// row is an SME issue even if an item arrives without its series.
const smeSubscriptionFeed = createSubscriptionFeed({
  stateKey: 'smeSubscription',
  typePrefix: 'smeSubscription/fetchSmeSubscriptionSummary',
  request: () => ipoService.getSmeSubscriptionSummary(),
  label: 'SME subscription data',
  mapRows: (rows) => rows.map((row) => ({ ...row, board: 'sme', boardLabel: 'SME' })),
});

export const fetchSubscriptionSummary = subscriptionFeed.fetchFeed;
// Sorted, display-ready rows for the landing-page subscription section and /ipo-subscription
export const selectSubscriptionRows = subscriptionFeed.selectRows;

export const fetchSmeSubscriptionSummary = smeSubscriptionFeed.fetchFeed;
// Sorted, display-ready SME rows for /sme-ipo-subscription
export const selectSmeSubscriptionRows = smeSubscriptionFeed.selectRows;
export const smeSubscriptionReducer = smeSubscriptionFeed.reducer;

export default subscriptionFeed.reducer;
